import {ErrorBoundary} from "@sentry/react";
import {BarEventStatus} from "@uptime-com/uptimebar";
import classNames from "classnames";
import {parseISO} from "date-fns";
import {FaXTwitter} from "react-icons/fa6";
import {FiHome, FiLink} from "react-icons/fi";
import {Link} from "react-router";

import {Card} from "@/components/card/Card.tsx";
import {CDNImage} from "@/components/cdnImage/CDNImage.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {IsItDownServiceStatus, IsItDownVariant, PublicProfile, PublicProfileStats} from "@/lib/types/isitdown.ts";
import {IsItDownStatusBadge} from "@/marketingPages/isitdown/components/isitdownstatusbadge/IsItDownStatusBadge.tsx";
import {
  PublicProfilesReportedIssues,
  PublicProfilesReportedIssuesProps,
} from "@/marketingPages/isitdown/components/reportedissues/PublicProfilesReportedIssues.tsx";
import {getParsedStats} from "@/marketingPages/isitdown/components/utils.ts";
import {CheckInfoBoxes} from "@/pages/checks/analysis/overview/checktypes/CheckInfoBoxes.tsx";
import {UptimeBarCheckAnalysis} from "@/pages/checks/components/details/UptimeBar/UptimeBarCheckAnalysis.tsx";

import styles from "./PublicProfilesServiceHealth.module.scss";

interface PublicProfilesServiceHealthProps extends PublicProfilesReportedIssuesProps {
  profile: PublicProfile;
  stats: PublicProfileStats;
  variant: IsItDownVariant;
}

export const PublicProfilesServiceHealth = ({
  profile,
  stats,
  timestamps,
  start,
  end,
  timezone,
  variant,
}: PublicProfilesServiceHealthProps) => {
  const parsedStats = getParsedStats(profile, stats);

  return (
    <div className={styles.container}>
      <div className="marketingTextS2Bold">Outages Reported – Last 24 hours</div>
      <Card variant="marketingWhite" className={styles.card}>
        <div className="p-2 text-start">
          <div className="d-flex gap-2 align-items-center">
            <div className={styles.imageContainer}>
              <CDNImage src={profile.image} domain={profile.slug} alt={profile.slug} title={profile.slug} />
            </div>
            <div className="pulseTextH2Bold">{profile.name || profile.slug}</div>
          </div>
          <Divider className="my-3" />
          <div className={styles.links}>
            <Link to={`https://${profile.slug}`} target="_blank">
              <Icon icon={FiHome} display="inline" />
              Home Page
            </Link>
            {profile.status_page && (
              <Link to={profile.status_page} target="_blank">
                <Icon icon={FiLink} display="inline" />
                Status Page
              </Link>
            )}
            {profile.twitter && (
              <Link to={`https://x.com/${profile.twitter.replace(/^@/, "")}`} target="_blank">
                <Icon icon={FaXTwitter} display="inline" />
                Status
              </Link>
            )}
          </div>
          <Divider className="mt-3 mb-4" />
          <div>
            <div className="pulseTextH3Bold mb-4">Uptime – Last 24 hours</div>
            <div className="d-flex align-items-center justify-content-between">
              <IsItDownStatusBadge serviceStatus={profile.service_status} />
              {parsedStats && (
                <div
                  className={classNames(
                    "pulseTextBodyBold",
                    profile.service_status === IsItDownServiceStatus.DOWN
                      ? "pulse-color-red-500"
                      : "pulse-color-green-500"
                  )}
                >
                  {parsedStats.uptimePercentage}
                </div>
              )}
            </div>
            <div className="mt-2 mb-4">
              {parsedStats && (
                <ErrorBoundary>
                  <UptimeBarCheckAnalysis
                    start={start}
                    end={end}
                    interval={parsedStats.service_msp_interval!}
                    createdAt={parseISO(parsedStats.service_created_at!)}
                    responseTimes={parsedStats.location_response_times || []}
                    intervalAlerts={parsedStats.intervalAlerts}
                    uptimeBarEvents={[]}
                    noResponseTimesDefaultStatus={
                      variant === IsItDownVariant.CHECKS ? BarEventStatus.NO_DATA : BarEventStatus.UP
                    }
                  />
                </ErrorBoundary>
              )}
            </div>
            {variant !== IsItDownVariant.CLOUDSTATUS && parsedStats && (
              <div className="mb-4">
                <CheckInfoBoxes
                  variant="marketing"
                  state={parsedStats.state}
                  since={parsedStats.since!}
                  outages={parsedStats.totals?.outages}
                  downtimeSecs={parsedStats.totals?.downtime_secs}
                  latestDowntime={parsedStats.latest_downtime!}
                />
              </div>
            )}
          </div>
          <Divider className="mb-4" />
          <div>
            <div className="pulseTextH3Bold mb-4">Reported Issues – Last 24 hours</div>
            <UpErrorBoundary>
              <PublicProfilesReportedIssues timestamps={timestamps} start={start} end={end} timezone={timezone} />
            </UpErrorBoundary>
          </div>
        </div>
      </Card>
    </div>
  );
};
